import React, { useState, useEffect } from 'react';
import { Drawer } from 'vaul';
import { useStats } from '@/hooks/useStats';
import { Loader2, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { CumulativePnlChart } from './CumulativePnlChart';

interface StatsDrawerProps {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const StatsDrawer: React.FC<StatsDrawerProps> = ({
  userId,
  open,
  onOpenChange,
}) => {
  const { data: stats, isLoading, error } = useStats(userId, open);
  // Use a key that changes when drawer opens to force chart remount
  const [chartKey, setChartKey] = useState(0);

  // Reset chart key when drawer opens
  useEffect(() => {
    if (open) {
      setChartKey(prev => prev + 1);
    }
  }, [open]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[120]" />
        <Drawer.Content
          className="bg-[#1a1b20] flex rounded-t-xl flex-col fixed bottom-0 left-0 right-0 max-h-[90vh] z-[121]"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
        >
          <div className="px-4 border-b border-gray-500/30 pb-3 pt-2 sticky top-0 bg-secondary z-10">
            <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
            <h2 className="text-lg font-bold text-text-primary mb-2">Trading Statistics</h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <p className="text-text-secondary text-sm">
                  Failed to load statistics. Please try again.
                </p>
              </div>
            )}

            {!isLoading && !error && stats && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-primary/40 border border-gray-600 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-text-secondary" />
                      <span className="text-xs text-text-secondary">Total Opened</span>
                    </div>
                    <p className="text-lg font-bold text-text-primary">
                      {stats.totalOpenedPositions}
                    </p>
                  </div>

                  <div className="bg-primary/40 border border-gray-600 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-text-secondary" />
                      <span className="text-xs text-text-secondary">Total Closed</span>
                    </div>
                    <p className="text-lg font-bold text-text-primary">
                      {stats.totalClosedPositions}
                    </p>
                  </div>

                  <div className="bg-primary/40 border border-gray-600 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-text-secondary" />
                      <span className="text-xs text-text-secondary">Positions Volume</span>
                    </div>
                    <p className="text-lg font-bold text-text-primary">
                      {formatCurrency(stats.totalUsdAmount)}
                    </p>
                  </div>

                  <div className={`bg-primary/40 border rounded-lg p-3 ${
                    stats.totalPnl >= 0 ? 'border-green-500/50' : 'border-red-500/50'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      {stats.totalPnl >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-xs text-text-secondary">Total PnL</span>
                    </div>
                    <p className={`text-lg font-bold ${
                      stats.totalPnl >= 0 ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {formatCurrency(stats.totalPnl)}
                    </p>
                  </div>
                </div>

                {/* Cumulative PnL Chart */}
                {stats.cumulativePnlData.length > 0 && (
                  <div className="bg-primary/40 border border-gray-600 rounded-lg p-4 relative">
                    <h3 className="text-sm font-bold text-text-primary mb-3">
                      Cumulative PnL (Last 30 Days)
                    </h3>
                    <CumulativePnlChart
                      key={chartKey}
                      cumulativePnlData={stats.cumulativePnlData}
                      positionMarkers={stats.positionMarkers}
                      height={250}
                    />
                  </div>
                )}

                {/* Best Position */}
                {stats.bestPosition && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-500" />
                      <h3 className="text-sm font-bold text-text-primary">Best Position</h3>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Symbol:</span>
                        <span className="text-text-primary font-semibold">
                          {stats.bestPosition.symbol}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Type:</span>
                        <span className="text-text-primary">{stats.bestPosition.positionType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">PnL:</span>
                        <span className="text-green-500 font-semibold">
                          {formatCurrency(stats.bestPosition.realizedPnl)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">PnL %:</span>
                        <span className="text-green-500 font-semibold">
                          {formatPercentage(stats.bestPosition.pnlPercentage)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Worst Position */}
                {stats.worstPosition && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="w-4 h-4 text-red-500" />
                      <h3 className="text-sm font-bold text-text-primary">Worst Position</h3>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Symbol:</span>
                        <span className="text-text-primary font-semibold">
                          {stats.worstPosition.symbol}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Type:</span>
                        <span className="text-text-primary">{stats.worstPosition.positionType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">PnL:</span>
                        <span className="text-red-500 font-semibold">
                          {formatCurrency(stats.worstPosition.realizedPnl)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">PnL %:</span>
                        <span className="text-red-500 font-semibold">
                          {formatPercentage(stats.worstPosition.pnlPercentage)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

